import { ElementType, ReactNode, useEffect, useRef, useState } from "react";

interface RevealProps {
  children: ReactNode;
  as?: ElementType;
  delay?: number;
  className?: string;
}

// Rises content into view once it scrolls on screen (after amicro's scroll-reveal, in CSS
// instead of framer-motion). The .reveal class has a still fallback for reduced motion.
export function Reveal({ children, as: Tag = "div", delay = 0, className = "" }: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ "--reveal-delay": `${delay}ms` } as React.CSSProperties}
    >
      {children}
    </Tag>
  );
}

// The curved edge between sections on burgerlijk.com
export function Wave({ fill, className = "" }: { fill: string; className?: string }) {
  return (
    <svg
      className={`block h-10 w-full md:h-16 ${className}`}
      viewBox="0 0 1440 80"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d="M0 40C240 80 480 80 720 48C960 16 1200 8 1440 40V80H0Z" fill={fill} />
    </svg>
  );
}
